import { io, Socket } from "socket.io-client";
import InitClientChatPacket from "./packets/client/InitClientChatPacket";
import ClientChatPacket from "./packets/ClientChatPacket";
import LogoutClientChatPacket from "./packets/client/LogoutClientChatPacket";
import { createApp, nextTick } from "vue";
import ChatTextInput from "./components/ChatTextInput";
import ChatLoginBox from "./components/ChatLoginBox";
import ChatAdminPane from "./admin/ChatAdminPane";
import { ChatItemHello } from "./components/items/Hello";
import { ChatMessageJson } from "./components/items/Message";
import ChatItem, { AbstractChatItem, ItemKind } from "./components/ChatItem";
import ChatItems from "./components/ChatItems";
import ChatAdminFooter from "./admin/ChatAdminFooter";

interface ChatOptions {
    id: number;
    key?: string;
    sid?: string;
    apiurl: string;
    css: string;

    i18n: any;
}

export enum ChatUserType {
    NORMAL = null,
    ADMIN = 1,
}

interface ChatUserJson {
    username: string;
    type: number;
    token: string;
}

interface LoadJson {
    hello?: string;
    username: string;

    messages: any[];

    user?: {
        id: number;
        name: string;
        token: string;
    };
}

interface LoginJson {
    username: string;
    token: string;
}

export default class Chat {
    private options: ChatOptions;

    private vue;

    private socket: Socket;

    private token: string;

    private inited: boolean = false;
    private visible: boolean;
    private h_screenenter: (() => void)[] = [];

    public constructor($uid: JQuery) {
        this.initHtml($uid);
    }

    private setToken(token: string): void {
        this.token = token;

        const key = 'chat-' + this.options.id;

        if (token) {
            localStorage.setItem(key, token);
        } else {
            localStorage.removeItem(key);
        }
    }

    public isVisible(): boolean {
        const $el = $(this.vue.$el);

        if (!$el.is(':visible')) {
            return false;
        }

        const top = $(window).scrollTop();
        const wh = window.innerHeight;

        const pos = $el.offset();
        const h = $el.height();

        if (top > pos.top + h || top < pos.top - wh) {
            return false;
        }

        return true;
    }

    public onScreenEnter(handler: () => void): void {
        this.h_screenenter.push(handler);
    }

    private doScreenEnter(): void {
        this.h_screenenter.forEach((v) => {
            v();
        });
    }

    public isOnEnd(): boolean {
        const $messages = $('.ChatItems', this.vue.$el);
        const $bodyinner = $('.livechat__bodyinner', this.vue.$el);

        const total = $messages.outerHeight();
        const wh = $bodyinner.outerHeight();
        const sh = $bodyinner.scrollTop();

        return sh + wh >= total - 10;
    }

    public gotoEnd(force: boolean = false): void {
        const $messages = $('.ChatItems', this.vue.$el);
        const $bodyinner = $('.livechat__bodyinner', this.vue.$el);

        const top = $messages.height();

        if (force) {
            $bodyinner.scrollTop(top);
        } else {
            $bodyinner.stop(true).animate({
                scrollTop: top
            });
        }
    }

    public async sendText(text: string) {
        this.socket.emit('msg', text);
    }

    public async sendPacket(packet: ClientChatPacket) {
        return packet.send(this.socket);
    }

    private updateScreen(): void {
        const visible = this.isVisible();

        if (visible != this.visible) {
            this.visible = visible;

            if (visible) {
                this.doScreenEnter();
            }
        }
    }

    private async loadMessages(messages: ChatMessageJson[]) {
        return new Promise<any>((resolve) => {
            const list = new Map<number, ChatMessageJson[]>();

            for (let ix = 0; ix < messages.length; ix++) {
                const msg = messages[ix];

                const k = Math.floor(ix / 10);

                if (!list.has(k)) {
                    list.set(k, []);
                }

                list.get(k).push(msg);
            }

            let lx = list.size;

            const pushList = () => {
                if (lx) {
                    const msgs = list.get(list.size - lx);

                    lx--;

                    msgs.forEach(msg => {
                        this.vue.$data.messages.push(msg);
                    });

                    setTimeout(pushList, 50);
                } else {
                    resolve(true);
                }
            };

            pushList();
        });
    }

    private initSocket(): void {
        this.socket.on('load', async (json: LoadJson) => {
            this.vue.$data.state = json;
            await this.vue.reload();
        });

        this.socket.on('login', (json: LoginJson) => {
            this.setToken(json.token);
            this.vue.$data.user = json;
        });

        this.socket.on('logout', (json) => {
            this.setToken(null);
            this.vue.$data.user = null;
        });

        this.socket.on('message', (json: ChatMessageJson) => {
            this.vue.pushJson(json);
        });

        this.socket.on('info', (json) => {
            this.vue.pushJson(json);
        });

        this.socket.on('admininfo', (json) => {
            this.vue.$data.admininfo = json;
        });

        this.socket.on('remove', (json) => {
            this.vue.removeMessage(json);
        });

        this.socket.on('update-message', (json) => {
            this.vue.updateMessage(json);
        });

        this.socket.on('reorder', (json) => {
            this.vue.reorder(json);
        });

        let reinit = false;

        this.socket.on('connect', () => {
            if (reinit) {
                this.sendPacket(new InitClientChatPacket(this.options.id, this.options.sid, this.options.key, this.token));

                reinit = false;
            }
        });

        this.socket.on('disconnect', (reason) => {
            this.vue.$data.inited = false;

            reinit = true;
        });
    }

    private initVue($uid: JQuery): void {
        const This = this;

        this.vue = createApp({
            components: {
                ChatAdminPane,
                ChatAdminFooter,
                ChatItem,
                ChatItems,
                ChatLoginBox,
                ChatTextInput,
            },

            template: `
                <div class="livechat" :class="{ 'livechat--withkey': options.key, 'livechat--inited': inited, 'livechat--sending': sending, 'livechat--logged': user, 'livechat--admin': admin }, options.css">
                    <div class="livechat__inner">
                        <div class="livechat__header">
                            <ChatAdminPane
                                v-if="admin"
                                v-model:hideremoved="hideremoved"
                            />

                            <div class="livechat__info"><b>{{ options.i18n.title }}</b></div>

                            <div v-if="user" class="livechat__user">
                                <button type="button" class="livechat__logout" @click="onLogout">wyloguj</button>
                            </div>
                        </div>

                        <div class="livechat__body skiptranslate">
                            <div class="livechat__bodyinner">
                                <ChatItems
                                    v-if="items"
                                    :items="items"
                                    :user="user"
                                    :admin="admin"
                                    :moderated="state?.moderated"
                                    :hideremoved="hideremoved"
                                />
                            </div>
                        </div>

                        <div class="livechat__footer">
                            <ChatAdminFooter
                                v-if="admin && admininfo"
                                :admininfo="admininfo"
                            />

                            <ChatTextInput
                                v-if="user"
                                :options="options"
                                :user="user"
                                v-model:sending="sending"
                            />

                            <ChatLoginBox
                                v-if="!user"
                                :options="options"
                                :username="state?.username"
                            />
                        </div>
                    </div>
                </div>
            `,

            data() {
                return {
                    options: This.options,

                    state: null,
                    admininfo: null,
                    socket: This.socket,

                    inited: false,
                    sending: false,
                    items: null,

                    user: null,
                    hideremoved: true,
                };
            },

            computed: {
                admin() {
                    return this.user?.type == ChatUserType.ADMIN;
                },
            },

            methods: {
                apiToItem(json): AbstractChatItem {
                    json.date = json.date != null ? new Date(json.date) : null;

                    return json;
                },

                pushJson(json): AbstractChatItem {
                    const items: AbstractChatItem[] = this.items;

                    const item = this.apiToItem(json);

                    items.push(item);

                    items.sort((a, b) => {
                        return a.date?.getTime() - b.date?.getTime();
                    });

                    this.items = items;

                    if (This.isOnEnd()) {
                        This.gotoEnd();
                    }

                    return item;
                },

                async reload() {
                    this.inited = false;

                    const items: AbstractChatItem[] = [];

                    for (const m of this.state.messages) {
                        items.push(this.apiToItem(m));
                    }

                    this.items = items;

                    this.user = this.state.user;

                    this.inited = true;

                    nextTick(() => {
                        This.gotoEnd(true);
                    });
                },

                onLogout() {
                    this.sendPacket(new LogoutClientChatPacket());
                },

                removeMessage(json: any) {
                    const items: ChatMessageJson[] = this.items.filter(v => v.kind == ItemKind.MESSAGE && json.messages.includes(v.id));

                    for (const m of items) {
                        m.removed = true;
                    }
                },

                updateMessage(json: ChatMessageJson) {
                    const fitems: ChatMessageJson[] = this.items.filter(v => v.kind == ItemKind.MESSAGE && json.id == v.id);

                    if (fitems.length) {
                        for (const m of fitems) {
                            for (const k in m) {
                                m[k] = null;
                            }

                            Object.assign(m, this.apiToItem(json));
                        }

                        if (This.isOnEnd()) {
                            This.gotoEnd();
                        }
                    } else {
                        this.pushJson(json);
                    }
                },

                reorder(json: ChatMessageJson) {
                    this.items.sort((a, b) => {
                        return a.date - b.date;
                    });
                },

                async sendPacket(packet: ClientChatPacket) {
                    return This.sendPacket(packet);
                },
            }
        }).mount($uid[0]);
    }

    private initHtml($uid: JQuery): void {
        this.options = $uid.data('options');

        this.socket = io(this.options.apiurl);

        this.token = localStorage.getItem('chat-' + this.options.id);

        this.initVue($uid);

        // this.$messages = $('.livechat__messages', $uid);
        // this.messages = new Messages(this, $('.livechat__messages', $uid));
        // this.$bodyinner = $('.livechat__bodyinner', $uid);

        // this.$text = $('.livechat__text-form', $uid);
        // this.$textform = $('.livechat__text-form', $uid);
        // this.$itext = $('.livechat__textinput', $uid);
        // this.$isend = $('.livechat__send', $uid);

        // this.$login = $('.livechat__login', $uid);
        // this.$loginform = $('.livechat__login-form', $uid);
        // this.$iusername = $('.livechat__username', $uid);

        // this.$logout = $('.livechat__logout', $uid);

        // this.$itext.on('keydown', (e) => {
        //     if (e.keyCode == 13) {
        //         e.preventDefault();
        //         this.send();
        //     }
        // });

        this.onScreenEnter(() => {
            if (!this.inited) {
                this.inited = true;

                this.sendPacket(new InitClientChatPacket(this.options.id, this.options.sid, this.options.key, this.token));
            }
        });

        $('.eventtabs__nav-item').bind('click', () => {
            setTimeout(() => {
                this.updateScreen();
            }, 1000);
        });

        $('.wseventlive__tabs-item button').bind('click', () => {
            setTimeout(() => {
                this.updateScreen();
            }, 1000);
        });

        $(window).on('scroll', () => {
            this.updateScreen();
        });

        $(window).on('resize', () => {
            this.updateScreen();
        });

        this.updateScreen();

        setTimeout(() => {
            this.updateScreen();
        }, 500);

        setInterval(() => {
            if (!this.vue.$data.inited) {
                this.sendPacket(new InitClientChatPacket(this.options.id, this.options.sid, this.options.key, this.token));
            }
        }, 15000);

        this.initSocket();
    }
}
